import { Button, message } from 'antd';
import { ExperimentOutlined, LogoutOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Persistent, hard-to-miss banner shown while impersonating another user
 * (Debug / Assume-User mode — NON-PRODUCTION ONLY).
 */
export default function ImpersonationBanner() {
  const { isImpersonating, currentUser, impersonator, stopImpersonation } = useAuth();

  if (!isImpersonating || !currentUser) return null;

  const actingAs = currentUser.full_name || currentUser.email;

  const handleStop = async () => {
    try {
      await stopImpersonation();
      window.location.reload();
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Could not stop impersonation');
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        flexWrap: 'wrap',
        padding: '8px 16px',
        // Mellow yellow — deliberately high-visibility so it's obvious you are
        // not acting as yourself. Dark text for contrast/accessibility.
        background: '#FFE066',
        color: '#5c3c00',
        borderBottom: '2px solid #FFC400',
        fontSize: 14,
        fontWeight: 600,
        position: 'sticky',
        top: 0,
        zIndex: 200,
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      }}
    >
      <ExperimentOutlined aria-hidden style={{ fontSize: 16 }} />
      <span>
        Debug Mode: Acting as <strong>{actingAs}</strong>
        {impersonator && (
          <span style={{ fontWeight: 400, opacity: 0.9 }}>
            {' '}— signed in as {impersonator.full_name || impersonator.email}
          </span>
        )}
      </span>
      <Button
        size="small"
        onClick={handleStop}
        icon={<LogoutOutlined />}
        aria-label="Stop assuming user and return to your own session"
        style={{
          background: '#5c3c00',
          borderColor: '#5c3c00',
          color: '#fff',
        }}
      >
        Stop
      </Button>
    </div>
  );
}
